var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Form, Formik } from "formik";
import { PeekButton, PeekDialogActions, PeekDialogContent, PeekDivider, PeekStack, useTheme, } from "@piiqtechnologies/ui-components";
import { useAnalytics, useCreateUser, useError, useLoader, useNotification, useRefetch, useUpdateUser, } from "@piiqtechnologies/ui-lib";
import { MultiColumnFormFields } from "@components/common/form/FormFields";
import { useAuthContext } from "@stores/AuthContext";
import { USER_ROLES } from "@constants/Roles";
import messages from "./messages.i18n";
import { CreateOrEditUserFormFields, UserFormValidationSchema } from "./CreateOrEditUserFormConfig";
import { PolicyGroups } from "@constants/Policies";
const BasicUserAllow = {
    insights: [],
};
const BasicUserDeny = {
    scanRequest: ["scan_request_edit" /* PolicyActions.SCAN_REQUEST_EDIT */, "scan_request_create" /* PolicyActions.SCAN_REQUEST_CREATE */],
    analytics: [],
    prospectIntelligence: [],
};
export const initializeAllow = (allow) => {
    return (allow || [])
        .map((action) => action.alias)
        .reduce((acc, action) => {
        if (PolicyGroups.INSIGHTS.includes(action)) {
            acc["insights"].push(action);
        }
        return acc;
    }, {
        insights: [],
    });
};
export const initializeDeny = (deny) => {
    return (deny || [])
        .map((action) => action.alias)
        .reduce((acc, action) => {
        if (PolicyGroups.SCAN_REQUEST.includes(action)) {
            acc["scanRequest"].push(action);
        }
        if (PolicyGroups.ANALYTICS.includes(action)) {
            acc["analytics"].push(action);
        }
        if (PolicyGroups.PROSPECT_INTELLIGENCE.includes(action)) {
            acc["prospectIntelligence"].push(action);
        }
        return acc;
    }, {
        scanRequest: [],
        analytics: [],
        prospectIntelligence: [],
    });
};
export const CreateOrEditUserForm = ({ isUserCreation = false, isAdminForm = false, onSubmit = () => ({}), userFormInitialValues, }) => {
    var _a, _b;
    const intl = useIntl();
    const theme = useTheme();
    const analytics = useAnalytics();
    const { user } = useAuthContext();
    const [createUser, userCreated, createUserInProgress, createUserError] = useCreateUser();
    const [updateUser, userUpdated, updateUserInProgress, updateUserError] = useUpdateUser();
    const [openCreateUser, setOpenCreateUser] = useState(false);
    const [isOwnerTypeForm, setIsOwnerTypeForm] = useState((userFormInitialValues === null || userFormInitialValues === void 0 ? void 0 : userFormInitialValues.roleId) === USER_ROLES.Owner);
    const [isInsightsTypeForm, setIsInsightsTypeForm] = useState((userFormInitialValues === null || userFormInitialValues === void 0 ? void 0 : userFormInitialValues.roleId) === USER_ROLES.Insights);
    const { setError } = useError();
    const { setLoader } = useLoader();
    const { setRefetch } = useRefetch();
    const { setNotification } = useNotification();
    const { createUser_row1, createUser_row2, createUser_row3 } = CreateOrEditUserFormFields({
        organizations: user === null || user === void 0 ? void 0 : user.organizations,
        communities: user === null || user === void 0 ? void 0 : user.communities,
        isAdminForm: isAdminForm,
        isOwnerTypeForm: isOwnerTypeForm,
        isInsightsTypeForm: isInsightsTypeForm,
    });
    const toggleCreateUser = () => {
        setOpenCreateUser(!openCreateUser);
    };
    const onFormSubmit = (values) => {
        const { _id, deny, allow } = values, userValues = __rest(values, ["_id", "deny", "allow"]);
        let organizations = [];
        if (userValues.roleId !== USER_ROLES.Insights) {
            organizations = userValues.roleId === USER_ROLES.Owner ? values.organizations : [values.organizations._id];
        }
        const userFormPayload = Object.assign(Object.assign(Object.assign(Object.assign({}, userValues), { organizations }), (userValues.roleId === USER_ROLES.Basic || userValues.roleId === USER_ROLES.Agent
            ? { deny: Object.values(deny).reduce((acc, curr) => [...acc, ...curr], []) }
            : {})), { allow: !!allow ? Object.values(allow).reduce((acc, curr) => [...acc, ...curr], []) : [] });
        if (isUserCreation) {
            createUser(userFormPayload);
            return;
        }
        updateUser(Object.assign({ _id: _id }, userFormPayload));
    };
    useEffect(() => {
        setLoader(createUserInProgress, "createUserInProgress");
        return () => setLoader(false, "createUserInProgress");
    }, [createUserInProgress]);
    useEffect(() => {
        setLoader(updateUserInProgress, "updateUserInProgress");
        return () => setLoader(false, "updateUserInProgress");
    }, [updateUserInProgress]);
    useEffect(() => {
        if (createUserError) {
            setError(createUserError === null || createUserError === void 0 ? void 0 : createUserError.message);
        }
    }, [createUserError]);
    useEffect(() => {
        if (updateUserError) {
            setError(updateUserError === null || updateUserError === void 0 ? void 0 : updateUserError.message);
        }
    }, [updateUserError]);
    useEffect(() => {
        if (userCreated || userUpdated) {
            toggleCreateUser();
            analytics.track(isUserCreation ? "User Created" : "User Updated");
            setNotification(isUserCreation
                ? intl.formatMessage(messages.createUserSuccess)
                : intl.formatMessage(messages.updateUserSuccess));
            onSubmit();
            setRefetch();
        }
    }, [userCreated, userUpdated]);
    const UserFormInitialValues = userFormInitialValues || {
        roleId: USER_ROLES.Basic,
        name: "",
        email: "",
        phone: "",
        fullAccess: true,
        allow: BasicUserAllow,
        deny: BasicUserDeny,
        organizations: !isAdminForm && user.role && { _id: (_b = (_a = user.organizations) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b._id },
    };
    return (React.createElement(Formik, { initialValues: UserFormInitialValues, validationSchema: UserFormValidationSchema(), onSubmit: onFormSubmit }, ({ values }) => {
        useEffect(() => {
            setIsOwnerTypeForm(values.roleId === USER_ROLES.Owner);
            setIsInsightsTypeForm(values.roleId === USER_ROLES.Insights);
        }, [values.roleId]);
        return (React.createElement(Form, null,
            React.createElement(PeekDialogContent, null,
                React.createElement(PeekStack, { direction: "column", spacing: theme.spacing(1) },
                    React.createElement(MultiColumnFormFields, { fullWidth: true, fields: createUser_row1 }),
                    React.createElement(PeekDivider, { light: true }),
                    React.createElement(MultiColumnFormFields, { fullWidth: true, fields: createUser_row2 }),
                    React.createElement(PeekDivider, { light: true }),
                    React.createElement(MultiColumnFormFields, { fullWidth: true, fields: createUser_row3 }))),
            React.createElement(PeekDialogActions, null,
                React.createElement(PeekButton, { size: "large", type: "submit", color: "primary", variant: "contained", fullWidth: true }, isUserCreation
                    ? intl.formatMessage(messages.createUserAction)
                    : intl.formatMessage(messages.editUserAction)))));
    }));
};
export default CreateOrEditUserForm;
